import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, BookOpen, StickyNote, Bookmark as BookmarkIcon, ScrollText, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { BookCover } from "../components/BookCover";
import { useAuth } from "../context/AuthContext";
import UploadDialog from "../components/UploadDialog";
import DailyGoal from "../components/DailyGoal";
import ShelvesSection from "../components/ShelvesSection";

function StatCard({ label, value, icon: Icon, testId }) {
  return (
    <div className="border border-border rounded-md bg-card p-5 paper-shadow" data-testid={testId}>
      <div className="flex items-center gap-2 font-ui text-xs tracking-caps text-muted-foreground">
        <Icon className="w-3.5 h-3.5 text-primary" /> {label}
      </div>
      <div className="font-display text-4xl md:text-5xl tracking-tight mt-2">{value}</div>
    </div>
  );
}

export default function Shelf() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);

  const loadAll = async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    try {
      const [p, s, mine] = await Promise.all([
        api.get("/progress"),
        api.get("/stats"),
        api.get("/books", { params: { mine: true, limit: 96 } }).catch(() => ({ data: { books: [] } })),
      ]);
      const rows = p.data.progress || [];
      setStats(s.data);
      setUploads(mine.data.books || []);
      const details = await Promise.all(
        rows.map((r) => api.get(`/books/${r.book_id}`).then((res) => res.data).catch(() => null))
      );
      const merged = rows.map((r, i) => ({ ...r, book: details[i] })).filter((x) => x.book);
      setItems(merged);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [user]);

  const deleteUpload = async (bookId) => {
    if (!window.confirm("remove this uploaded book from your shelf?")) return;
    try {
      await api.delete(`/books/${bookId}`);
      setUploads((v) => v.filter((b) => b.id !== bookId));
      setItems((v) => v.filter((it) => it.book_id !== bookId));
      toast.success("removed.");
    } catch {
      toast.error("could not remove");
    }
  };

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <div className="font-display text-4xl md:text-5xl tracking-tight">an empty shelf awaits.</div>
        <p className="font-reader italic text-muted-foreground mt-4">sign in to start collecting.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-14 pb-32">
      <h1 className="font-display text-4xl md:text-5xl tracking-tight lowercase">my shelf</h1>
      <p className="font-reader text-muted-foreground mt-2 italic">
        volumes you&apos;ve cracked open — and how the reading is going.
      </p>

      <DailyGoal className="mt-8" />
      <Link to="/stats" data-testid="view-stats-link" className="inline-block mt-3 font-ui text-xs tracking-caps text-primary hover:underline underline-offset-4">see all reading stats →</Link>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8" data-testid="reading-stats">
          <StatCard label="current streak" value={`${stats.current_streak_days}d`} icon={Flame} testId="stat-streak" />
          <StatCard label="books opened" value={stats.books_started} icon={BookOpen} testId="stat-books" />
          <StatCard label="pages turned" value={stats.pages_read.toLocaleString()} icon={ScrollText} testId="stat-pages" />
          <StatCard label="notes & marks" value={stats.notes_count + stats.bookmarks_count} icon={StickyNote} testId="stat-notes" />
        </div>
      )}

      <div className="rule-ornament font-ui text-xs tracking-caps my-10">
        <span>{loading ? "gathering…" : `${items.length} currently reading`}</span>
      </div>

      {items.length === 0 && !loading && (
        <div className="font-reader text-muted-foreground italic py-16 text-center">
          nothing yet. <Link to="/" className="text-primary underline underline-offset-4">wander the shelves →</Link>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-6 gap-y-10">
        {items.map((it) => (
          <Link to={`/read/${it.book.id}?p=${it.page}`} key={it.book_id} className="block group" data-testid={`shelf-item-${it.book.id}`}>
            <BookCover book={it.book} />
            <div className="mt-3">
              <div className="font-display text-base leading-tight group-hover:text-primary transition-colors line-clamp-2">
                {it.book.title}
              </div>
              <div className="font-ui text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <BookmarkIcon className="w-3 h-3" /> page {it.page + 1}
              </div>
            </div>
          </Link>
        ))}
      </div>

      <ShelvesSection />

      <div className="mt-20 flex items-center justify-between flex-wrap gap-4">
        <h2 className="font-display text-3xl md:text-4xl tracking-tight lowercase">your uploads</h2>
        <button
          onClick={() => setUploadOpen(true)}
          data-testid="upload-book-btn"
          className="inline-flex items-center gap-2 font-ui text-xs tracking-caps px-4 py-2 rounded-full border border-border hover:bg-secondary transition-colors"
        >
          <Upload className="w-3.5 h-3.5" /> upload a book
        </button>
      </div>
      <div className="rule-ornament font-ui text-xs tracking-caps my-6">
        <span>{uploads.length ? `${uploads.length} private volumes` : "your private shelf is empty"}</span>
      </div>

      {uploads.length === 0 ? (
        <div className="font-reader italic text-muted-foreground py-8">
          drop in a .txt or .epub you own — it stays private to you and works with every reader tool.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-6 gap-y-10">
          {uploads.map((b) => (
            <div key={b.id} className="group relative" data-testid={`upload-item-${b.id}`}>
              <Link to={`/book/${b.id}`} className="block">
                <BookCover book={b} />
                <div className="mt-3">
                  <div className="font-display text-base leading-tight group-hover:text-primary transition-colors line-clamp-2">
                    {b.title}
                  </div>
                  <div className="font-ui text-xs text-muted-foreground mt-1 line-clamp-1">{b.author}</div>
                </div>
              </Link>
              <button
                onClick={(e) => { e.preventDefault(); deleteUpload(b.id); }}
                data-testid={`delete-upload-${b.id}`}
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-background/90 border border-border rounded-full p-1.5 hover:text-destructive"
                title="remove"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <UploadDialog open={uploadOpen} onOpenChange={(o) => { setUploadOpen(o); if (!o) loadAll(); }} />
    </div>
  );
}
